import type { I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { ExternalLink } from 'lucide-react';
import { useFormContext, type FieldValues, type Path } from 'react-hook-form';
import { z } from 'zod';

import { choiceOptions, columnsFor, enumLabels, type ColumnSpec, type EnumLike } from '@/shared/patterns';

import { CollectionField, type CollectionTexts } from './collection-field';
import { EntityPickerField } from './entity-picker';
import type { EntitySource } from './entity-source';
import { SelectFormField } from './fields';

/** Um envolvido na lista do formulário: pessoa, relação e, quando assina, forma de envio e de validação. */
export interface InvolvedValues {
  personId: string;
  personIdDisplay?: string | null | undefined;
  discriminator?: string | null | undefined;
  signatureType?: string | null | undefined;
  signatureTypeDisplay?: string | null | undefined;
  signatureValidation?: string | null | undefined;
  signatureValidationDisplay?: string | null | undefined;
  [field: string]: unknown;
}

type Options = EnumLike;

export interface InvolvedOptions {
  /** Campo da relação: `typeOfRelationship` (Crédito, Produto) ou `fundSignerType` (Cessionários). */
  readonly relationField?: string;
  /** Com forma de envio e de validação da assinatura (Crédito e Cessionários, 06 §3.d). */
  readonly withSignature?: boolean;
}

const DEFAULT_RELATION = 'typeOfRelationship';
// eslint-disable-next-line lingui/no-unlocalized-strings -- sufixo de campo do fio (`fundSignerTypeDisplay`), não texto de UI
const DISPLAY = 'Display';

/**
 * Schema do envolvido (P13), com as mensagens do legado (U20-5 corrige a caixa). Mantém as chaves
 * que o `GET` trouxe (`looseObject`): o item volta ao fio como veio, mais o que mudou.
 */
export function involvedSchema(i18n: I18n, options: InvolvedOptions = {}): z.ZodType<InvolvedValues, InvolvedValues> {
  const relation = options.relationField ?? DEFAULT_RELATION;
  const required = (text: string) => z.string({ error: text }).min(1, text);
  const optional = z.string().nullish();
  return z.looseObject({
    personId: required(i18n._(msg`Envolvido: precisa ser preenchido.`)),
    personIdDisplay: optional,
    [relation]: required(i18n._(msg`Tipo de relação: precisa ser preenchido.`)),
    [`${relation}${DISPLAY}`]: optional,
    signatureType: optional,
    signatureTypeDisplay: optional,
    signatureValidation: optional,
    signatureValidationDisplay: optional,
  }) as unknown as z.ZodType<InvolvedValues, InvolvedValues>;
}

/** Valores do "Adicionar envolvido": tudo `null`, como o `defaultValuesInvolvedFundForm` do legado. */
export function involvedEmpty(options: InvolvedOptions = {}): InvolvedValues {
  const relation = options.relationField ?? DEFAULT_RELATION;
  return { personId: '', personIdDisplay: null, [relation]: null, [`${relation}${DISPLAY}`]: null, signatureType: null, signatureValidation: null };
}

const labelsOf = enumLabels;

/**
 * Colunas da lista de envolvidos: nome, relação e, com assinatura, forma de envio e de validação,
 * rotuladas pelo valor técnico (U33) com o `*Display` do fio como reserva.
 */
export function involvedColumns(options: InvolvedOptions & { readonly relation: Options; readonly signatureTypes?: Options; readonly signatureValidations?: Options }): ColumnSpec[] {
  const relation = options.relationField ?? DEFAULT_RELATION;
  const col = columnsFor<Record<string, unknown>>();
  const columns: ColumnSpec[] = [
    col.text('personIdDisplay', msg`Nome`),
    col.enum(relation, msg`Tipo de relação`, labelsOf(options.relation), { fallbackField: `${relation}${DISPLAY}` }),
  ];
  if (options.withSignature) {
    columns.push(col.enum('signatureType', msg`Forma de envio`, options.signatureTypes ? labelsOf(options.signatureTypes) : {}, { fallbackField: 'signatureTypeDisplay' }));
    columns.push(col.enum('signatureValidation', msg`Forma de validação`, options.signatureValidations ? labelsOf(options.signatureValidations) : {}, { fallbackField: 'signatureValidationDisplay' }));
  }
  return columns;
}

export interface InvolvedFieldsProps<T> extends InvolvedOptions {
  readonly person: EntitySource<T>;
  readonly relation: Options;
  readonly signatureTypes?: Options;
  readonly signatureValidations?: Options;
  /** Campos extras a partir da pessoa escolhida (o discriminador PF/PJ, para abrir o cadastro). */
  readonly personExtras?: (item: T | null) => Record<string, unknown>;
}

/**
 * Campos de um envolvido (P13, 06 §3.d) dentro do `EntitySheet`: pessoa por busca remota, relação e,
 * com `withSignature`, forma de envio e de validação. Cada escolha grava também o `*Display`, como o
 * `SelectFormField` do legado.
 */
export function InvolvedFields<T>({ person, relation, signatureTypes, signatureValidations, personExtras, relationField = DEFAULT_RELATION, withSignature = false }: InvolvedFieldsProps<T>) {
  const { i18n, t } = useLingui();
  const { setValue } = useFormContext<InvolvedValues>();
  const display = (name: string) => (_value: string, option: { readonly label: unknown } | undefined) =>
    setValue(`${name}${DISPLAY}`, typeof option?.label === 'string' ? option.label : null, { shouldDirty: true });
  return (
    <>
      <EntityPickerField<InvolvedValues, T>
        name="personId"
        displayName="personIdDisplay"
        label={t`Envolvido`}
        required
        placeholder={t`Buscar por nome, CPF ou CNPJ`}
        source={person}
        onSelect={(item, set) => {
          for (const [key, value] of Object.entries(personExtras?.(item) ?? {})) set(key, value);
        }}
      />
      <SelectFormField<InvolvedValues>
        name={relationField}
        label={t`Tipo de relação`}
        required
        options={choiceOptions(i18n, relation)}
        onValueChange={display(relationField)}
      />
      {withSignature ? (
        <>
          <SelectFormField<InvolvedValues> name="signatureType" label={t`Forma de envio`} options={signatureTypes ? choiceOptions(i18n, signatureTypes) : []} onValueChange={display('signatureType')} />
          <SelectFormField<InvolvedValues>
            name="signatureValidation"
            label={t`Forma de validação`}
            options={signatureValidations ? choiceOptions(i18n, signatureValidations) : []}
            onValueChange={display('signatureValidation')}
          />
        </>
      ) : null}
    </>
  );
}

export interface InvolvedListFieldProps<V extends FieldValues, T> extends InvolvedFieldsProps<T> {
  /** O array no formulário pai (`relatedPersonFund`, `relatedPersons`). */
  readonly name: Path<V>;
  /** Nome do sheet na URL (padrão `envolvido`). */
  readonly sheet?: string;
  /** "Abrir cadastro da pessoa" (a lupa do legado), que aparece também em somente leitura. */
  readonly onOpenPerson?: (item: InvolvedValues) => void;
  readonly texts?: Partial<CollectionTexts<InvolvedValues>>;
  readonly readOnly?: boolean;
}

/**
 * Aba Envolvidos pronta (P12 + P13): a lista com adicionar, editar e remover com confirmação (U20-3),
 * sem chamada ao servidor; o array vai no Salvar do pai. A mesma pessoa não entra duas vezes.
 */
export function InvolvedListField<V extends FieldValues, T>({ name, sheet = 'envolvido', onOpenPerson, texts, readOnly, ...fields }: InvolvedListFieldProps<V, T>) {
  const { i18n, t } = useLingui();
  const options: InvolvedOptions = { ...(fields.relationField ? { relationField: fields.relationField } : {}), ...(fields.withSignature ? { withSignature: true } : {}) };
  const merged: CollectionTexts<InvolvedValues> = {
    title: t`Envolvidos`,
    count: (count) => (count === 1 ? t`1 pessoa` : t`${count} pessoas`),
    add: t`Adicionar envolvido`,
    emptyTitle: t`Nenhum envolvido adicionado`,
    emptyDescription: t`Cedente, cessionário, testemunhas e agentes de cobrança que assinam entram aqui.`,
    sheetAddTitle: t`Adicionar envolvido`,
    sheetEditTitle: t`Editar envolvido`,
    sheetDescription: t`Confira as informações antes de salvar o envolvido.`,
    submitAdd: t`Adicionar envolvido`,
    submitEdit: t`Salvar envolvido`,
    itemName: (item) => item.personIdDisplay ?? item.personId,
    removeTitle: (item) => t`Remover ${item.personIdDisplay ?? item.personId}?`,
    removeDescription: () => t`A pessoa sai da lista de envolvidos. A mudança só vale quando você salvar o cadastro.`,
    removeConfirm: t`Sim, remover`,
    saved: t`Lista de envolvidos atualizada`,
    removed: t`Envolvido removido da lista`,
    duplicate: t`Essa pessoa já está na lista de envolvidos.`,
    ...texts,
  };
  return (
    <CollectionField<V, InvolvedValues>
      name={name}
      texts={merged}
      columns={involvedColumns({ ...options, relation: fields.relation, ...(fields.signatureTypes ? { signatureTypes: fields.signatureTypes } : {}), ...(fields.signatureValidations ? { signatureValidations: fields.signatureValidations } : {}) })}
      getKey={(item) => item.personId}
      schema={involvedSchema(i18n, options)}
      empty={involvedEmpty(options)}
      sheet={sheet}
      duplicateField="personId"
      {...(readOnly ? { readOnly } : {})}
      {...(onOpenPerson ? { rowActions: [{ id: 'open', always: true, icon: ExternalLink, label: () => t`Abrir cadastro da pessoa`, onSelect: (item) => onOpenPerson(item) }] } : {})}
      renderForm={() => <InvolvedFields {...fields} />}
    />
  );
}
